import { useEffect, useRef } from 'react';

/** Idle time after which `onTimeout` fires. */
export const INACTIVITY_TIMEOUT_MS = 4 * 60 * 60 * 1000; // 4 hours

const CHECK_INTERVAL_MS = 30 * 1000;
const ACTIVITY_EVENTS = ['mousedown', 'mousemove', 'keydown', 'wheel', 'touchstart', 'scroll'] as const;

/** Fires `onTimeout` once, after `timeoutMs` with no mouse/keyboard/touch/
 *  scroll activity anywhere on the page (and no tab switch back into view).
 *  Cheap by design — activity only stamps a ref, no re-render and no timer
 *  reset per event; a low-frequency interval checks elapsed time instead.
 *  The clock (re)starts whenever `enabled` turns on, so a fresh sign-in
 *  always gets the full window. */
export function useInactivityLogout(
  enabled: boolean,
  onTimeout: () => void,
  timeoutMs: number = INACTIVITY_TIMEOUT_MS,
) {
  const lastActivity = useRef(Date.now());
  const firedRef = useRef(false);

  useEffect(() => {
    if (!enabled) return;
    lastActivity.current = Date.now();
    firedRef.current = false;

    const markActive = () => {
      lastActivity.current = Date.now();
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible') markActive();
    };

    for (const evt of ACTIVITY_EVENTS) window.addEventListener(evt, markActive, { passive: true });
    document.addEventListener('visibilitychange', onVisible);

    const interval = window.setInterval(() => {
      if (!firedRef.current && Date.now() - lastActivity.current >= timeoutMs) {
        firedRef.current = true;
        onTimeout();
      }
    }, CHECK_INTERVAL_MS);

    return () => {
      for (const evt of ACTIVITY_EVENTS) window.removeEventListener(evt, markActive);
      document.removeEventListener('visibilitychange', onVisible);
      window.clearInterval(interval);
    };
  }, [enabled, onTimeout, timeoutMs]);
}
